import React, { useState, useRef, useEffect, useContext } from 'react';
import './ChatPopup.css';
import ENDPOINT from './data/ENDPOINT';
import { AuthContext } from './context/AuthContext';
import SecurityHeaders from './data/SecurityHeaders';
import axios from 'axios';

const ChatPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef(null);
  const { user } = useContext(AuthContext);
  const [unread, setUnread] = useState(0);
  const prevMessagesRef = useRef([]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Calculate unread messages
  const calculateUnread = (msgs) => {
    const unreadCount = msgs.filter((msg) => msg.role !== 'student' && msg.is_read === '0').length;
    setUnread(unreadCount);
  };

  // Play notification sound for new unread messages
  const playNotificationSound = () => {
    const audio = document.getElementById('audio');
    if (audio) {
      audio.play().catch((error) => console.error('Error playing sound:', error));
    }
  };

  // Fetch messages from PHP backend
  const fetchMessages = async () => {
    try {
      const formData = new FormData();
      formData.append('session', user.SessionId);
      const response = await axios.post(`${ENDPOINT}get_messages.php`, formData, SecurityHeaders);
      const data = response.data;
      if (data.status === 'success') {
        const newMessages = data.messages;
        setMessages(newMessages);
        // Scroll to bottom if chat is open
        if (isOpen && messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        // Check for new unread messages
        const prevMessages = prevMessagesRef.current;
        const newUnreadMessages = newMessages.filter(
          (msg) =>
            msg.role !== 'student' &&
            msg.is_read === '0' &&
            !prevMessages.some((prevMsg) => prevMsg.id === msg.id)
        );
        if (newUnreadMessages.length > 0 && !isOpen) {
          playNotificationSound();
        }

        // Update unread count
        calculateUnread(newMessages);
        // Update previous messages
        prevMessagesRef.current = newMessages;
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Mark message as read
  const markMessageAsRead = async (messageId) => {
    try {
      const formData = new FormData();
      formData.append('session', user.SessionId);
      formData.append('message_id', messageId);
      await axios.post(
        `${ENDPOINT}mark_message_read.php`,
        formData,
        SecurityHeaders
      );
      // Update local messages to reflect read status
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId ? { ...msg, is_read: '1' } : msg
        )
      );
      calculateUnread(messages);
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Poll for messages every 5 seconds when chat is open
  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Mark all unread messages as read when chat is opened
  useEffect(() => {
    if (isOpen) {
      const unreadMessages = messages.filter(
        (msg) => msg.role !== 'student' && msg.is_read === '0'
      );
      console.log(unreadMessages);
      unreadMessages.forEach((msg) => markMessageAsRead(msg.id));
    }
  }, [isOpen, messages]);
  // console.log(isOpen, messages);

  // Send message to PHP backend
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !user.SessionId) return;

    try {
      const formData = new FormData();
      formData.append('session', user.SessionId);
      formData.append('message', message);
      const response = await axios.post(
        `${ENDPOINT}send_message.php`,
        formData,
        SecurityHeaders
      );
      const data = response.data;
      if (data.status === 'success') {
        setMessage('');
        fetchMessages();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  return (
    <div className="chat-container">
      <button
        className="chat-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        title={isOpen ? 'Close Chat' : 'Open Chat'}
      >
        💬
        {unread > 0 && <span className="unread-badge">{unread}</span>}
      </button>
      {isOpen && (
        <div className="chat-popup">
          <div className="chat-header">
            <img
              src="https://ui-avatars.com/api/?name=Support&background=25d366&color=fff"
              alt="Avatar"
              className="chat-avatar"
            />
            Support Chat
          </div>
          <div
            className="chat-messages"
            style={{
              height: '60vh',
              maxHeight: '60vh',
              overflowY: 'auto',
              background: '#ece5dd',
            }}
          >
            {[...messages]
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
              .map((msg, index) => {
                const isOwn = msg.role === 'student';
                return (
                  <div
                    key={msg.id}
                    className={`chat-message ${isOwn ? 'own-message' : 'other-message'}`}
                  >
                    <div className="chat-meta">
                      <span>{msg.username}</span>
                      <span className="chat-time">
                        {new Date(msg.created_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <div>{msg.message}</div>
                    {!isOwn && msg.is_read === '0' && (
                      <span className="unread-indicator">Unread</span>
                    )}
                  </div>
                );
              })}
            <div ref={messagesEndRef} />
          </div>
          <form onSubmit={sendMessage} className="chat-form">
            <input
              type="text"
              placeholder="Type a message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="chat-input"
            />
            <button type="submit" className="chat-send-btn" title="Send">
              ▶
            </button>
          </form>
        </div>
      )}
      <audio id="audio" src="/notification.mp3" style={{ display: 'none' }} />
    </div>
  );
};

export default ChatPopup;